import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { pingAPI } from "@/lib/ping-api";
import { queryKeys } from "@/lib/query-keys";
import type { MessageListParams } from "@/types";

// Hook for fetching messages for a specific chat
export const usePingMessages = (
  chatId: string,
  params?: Omit<MessageListParams, "chat_id">
) => {
  const messageParams = {
    chat_id: chatId,
    ...params,
    consolidated: "true",
  };

  const query = useQuery({
    queryKey: queryKeys.ping.messages.list(chatId, params),
    queryFn: () => pingAPI.messages.list(messageParams),
    select: (data) => ({
      messages: data.results || [],
      pagination: {
        count: data.count,
        next: data.next,
        previous: data.previous,
      },
    }),
    enabled: !!chatId,
    retry: false,
    staleTime: 15000, // 15 seconds (shorter for messages)
    gcTime: 300000, // 5 minutes
  });

  return query;
};

// Helper function to extract page number from next URL
const getPageFromUrl = (url: string | null): number => {
  if (!url) return 1;
  const urlObj = new URL(url);
  const page = urlObj.searchParams.get("page");
  return page ? parseInt(page, 10) : 1;
};

// Hook for infinite scrolling messages
// NOTE: Uses queryKeys from query-keys.ts for consistency
// When refetching from real-time events, invalidate using queryKeys.ping.messages.infinite(chatId)
export const usePingMessagesInfinite = (
  chatId: string,
  pageSize: number = 50
) => {
  return useInfiniteQuery({
    queryKey: queryKeys.ping.messages.infiniteWithParams(chatId, pageSize),
    queryFn: async ({ pageParam = 1 }) => {
      const response = await pingAPI.messages.list({
        chat_id: chatId,
        consolidated: "true",
        page: pageParam,
        page_size: pageSize,
      });
      return response;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      // Extract next page number from the next URL
      if (lastPage.next) {
        return getPageFromUrl(lastPage.next);
      }
      return undefined;
    },
    getPreviousPageParam: (firstPage) => {
      // For reverse pagination (older messages)
      if (firstPage.previous) {
        return getPageFromUrl(firstPage.previous);
      }
      return undefined;
    },
    select: (data) => {
      // Flatten all pages into a single array
      const allMessages = data.pages.flatMap((page) => page.results || []);

      // Deduplicate messages by ID (in case of overlapping pages or race conditions)
      const uniqueMessages = Array.from(
        new Map(allMessages.map((msg) => [msg.id, msg])).values()
      );

      return {
        messages: uniqueMessages,
        pagination: {
          count: data.pages[0]?.count || 0,
          next: data.pages[data.pages.length - 1]?.next || null,
          previous: data.pages[0]?.previous || null,
        },
        pages: data.pages,
      };
    },
    enabled: !!chatId,
    retry: false,
    staleTime: 15000, // 15 seconds
    gcTime: 300000, // 5 minutes
  });
};

// Hook for fetching messages with real-time updates
export const usePingMessagesWithRefresh = (
  chatId: string,
  params?: Omit<MessageListParams, "chat_id">,
  refreshInterval?: number
) => {
  const messageParams = {
    chat_id: chatId,
    ...params,
    consolidated: "true",
  };

  return useQuery({
    queryKey: queryKeys.ping.messages.list(chatId, params),
    queryFn: () => pingAPI.messages.list(messageParams),
    select: (data) => data.results || [],
    enabled: !!chatId,
    retry: false,
    refetchInterval: refreshInterval || 10000, // Default 10 seconds for active chat
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
};

// Hook for fetching initial messages (useful for chat initialization)
export const usePingInitialMessages = (
  chatId: string,
  pageSize: number = 20
) => {
  return useQuery({
    queryKey: queryKeys.ping.messages.list(chatId, {
      page: 1,
      page_size: pageSize,
    }),
    queryFn: () =>
      pingAPI.messages.list({
        chat_id: chatId,
        consolidated: "true",
        page: 1,
        page_size: pageSize,
      }),
    select: (data) => data.results || [],
    enabled: !!chatId,
    retry: false,
    staleTime: 10000, // 10 seconds
  });
};
